import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import { SingleColumnTemplate } from '../../../../../ui/layouts';
import Button from '../../../../../ui/canon/Button';
import Card from '../../../../../ui/canon/Card';
import EmptyState from '../../../../../ui/canon/EmptyState';
import { Badge } from '../shared';
import {
  Briefcase,
  Users,
  Eye,
  MessageSquare,
  Plus,
  Info as InfoIcon,
} from 'lucide-react';

// Rich empty state shown when user has zero hiring posts
const HiringEmptyState = () => {
  const intl = useIntl();

  const features = [
    {
      icon: Users,
      title: intl.formatMessage({ id: 'hiring.my_posts.empty_state.feature_reach_title' }),
      description: intl.formatMessage({ id: 'hiring.my_posts.empty_state.feature_reach_description' }),
    },
    {
      icon: Eye,
      title: intl.formatMessage({ id: 'hiring.my_posts.empty_state.feature_track_title' }),
      description: intl.formatMessage({ id: 'hiring.my_posts.empty_state.feature_track_description' }),
    },
    {
      icon: MessageSquare,
      title: intl.formatMessage({ id: 'hiring.my_posts.empty_state.feature_connect_title' }),
      description: intl.formatMessage({ id: 'hiring.my_posts.empty_state.feature_connect_description' }),
    },
  ];

  return (
    <div className="flex flex-col items-center py-6 px-4">
      {/* Main bordered card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-succession-bg-secondary rounded-2xl border border-succession-border p-8 md:p-10 max-w-2xl text-center"
      >
        {/* Icon */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-succession-primary/10 mb-6"
        >
          <Briefcase className="w-10 h-10 text-succession-primary" />
        </motion.div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-2xl md:text-3xl font-bold mb-4 text-succession-text-primary"
        >
          {intl.formatMessage({ id: 'hiring.my_posts.empty_state.title' })}
        </motion.h2>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-succession-text-secondary text-lg mb-6 leading-relaxed"
        >
          {intl.formatMessage({ id: 'hiring.my_posts.empty_state.description' })}
        </motion.p>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-left"
        >
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-succession-bg-primary">
              <feature.icon className="w-5 h-5 text-succession-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-succession-text-primary text-sm">{feature.title}</p>
                <p className="text-xs text-succession-text-secondary">{feature.description}</p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <a
            href="/hiring/new"
            className="inline-flex items-center gap-2 bg-succession-primary hover:bg-succession-primary/90 text-white px-8 py-3 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            {intl.formatMessage({ id: 'hiring.my_posts.empty_state.cta' })}
            <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>

          <p className="text-sm text-succession-text-secondary mt-4">
            {intl.formatMessage({ id: 'hiring.my_posts.empty_state.cta_note' })}
          </p>
        </motion.div>
      </motion.div>

      {/* Info Banner - Outside the bordered card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.5 }}
        className="bg-succession-primary/5 border border-succession-primary/20 rounded-lg p-4 mt-6 max-w-2xl w-full"
      >
        <div className="flex items-start gap-3">
          <InfoIcon className="w-5 h-5 text-succession-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-succession-text-primary mb-1">
              {intl.formatMessage({ id: 'hiring.my_posts.empty_state.info_title' })}
            </p>
            <p className="text-succession-text-secondary">
              {intl.formatMessage({ id: 'hiring.my_posts.empty_state.info_body' })}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const MyHiringPosts = ({ posts, hideHeader }) => {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState('active');

  // Show the rich empty state when the user has no posts at all
  if (!posts || posts.length === 0) {
    if (hideHeader) return <HiringEmptyState />;
    return (
      <SingleColumnTemplate
        title={intl.formatMessage({ id: 'hiring.my_posts.title' })}
        subtitle={intl.formatMessage({ id: 'hiring.my_posts.subtitle_empty' })}
      >
        <HiringEmptyState />
      </SingleColumnTemplate>
    );
  }

  const activePosts = posts.filter(post => post.status === 'published');
  const draftPosts = posts.filter(post => post.status === 'draft');
  const closedPosts = posts.filter(post => post.status === 'closed');

  const currentPosts =
    activeTab === 'active'
      ? activePosts
      : activeTab === 'draft'
        ? draftPosts
        : closedPosts;

  const totalViews = activePosts.reduce((sum, post) => sum + (post.viewsCount || 0), 0);
  const totalConversations = activePosts.reduce((sum, post) => sum + (post.conversationsCount || 0), 0);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const content = (
    <>
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card padding="md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500 mb-1">{intl.formatMessage({ id: 'hiring.my_posts.stats.active_posts' })}</p>
                <p className="text-2xl font-bold text-neutral-900">{activePosts.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </Card>

          <Card padding="md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500 mb-1">{intl.formatMessage({ id: 'hiring.my_posts.stats.total_views' })}</p>
                <p className="text-2xl font-bold text-neutral-900">{totalViews}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
            </div>
          </Card>

          <Card padding="md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500 mb-1">{intl.formatMessage({ id: 'hiring.my_posts.stats.conversations' })}</p>
                <p className="text-2xl font-bold text-neutral-900">{totalConversations}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
            </div>
          </Card>

          <Card padding="md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500 mb-1">{intl.formatMessage({ id: 'hiring.my_posts.stats.avg_response' })}</p>
                <p className="text-2xl font-bold text-neutral-900">2.3d</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="border-b border-neutral-200 mb-6">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('active')}
              className={`pb-3 px-1 border-b-2 transition-colors ${
                activeTab === 'active'
                  ? 'border-blue-600 text-blue-600 font-medium'
                  : 'border-transparent text-neutral-600 hover:text-neutral-900'
              }`}
            >
              {intl.formatMessage({ id: 'hiring.my_posts.tab.active' }, { count: activePosts.length })}
            </button>
            <button
              onClick={() => setActiveTab('draft')}
              className={`pb-3 px-1 border-b-2 transition-colors ${
                activeTab === 'draft'
                  ? 'border-blue-600 text-blue-600 font-medium'
                  : 'border-transparent text-neutral-600 hover:text-neutral-900'
              }`}
            >
              {intl.formatMessage({ id: 'hiring.my_posts.tab.drafts' }, { count: draftPosts.length })}
            </button>
            <button
              onClick={() => setActiveTab('closed')}
              className={`pb-3 px-1 border-b-2 transition-colors ${
                activeTab === 'closed'
                  ? 'border-blue-600 text-blue-600 font-medium'
                  : 'border-transparent text-neutral-600 hover:text-neutral-900'
              }`}
            >
              {intl.formatMessage({ id: 'hiring.my_posts.tab.closed' }, { count: closedPosts.length })}
            </button>
          </div>
        </div>

        {/* Posts List */}
        {currentPosts.length > 0 ? (
          <div className="space-y-4">
            {currentPosts.map((post) => (
              <Card key={post.id} variant="interactive" padding="lg">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Post Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge
                        variant={post.positionType === 'internship' ? 'internship' : 'full-time'}
                        size="sm"
                      >
                        {post.positionType === 'internship'
                          ? intl.formatMessage({ id: 'hiring.my_posts.post.type.internship' })
                          : intl.formatMessage({ id: 'hiring.my_posts.post.type.full_time' })}
                      </Badge>
                      <Badge
                        variant={
                          post.status === 'published'
                            ? 'success'
                            : post.status === 'draft'
                              ? 'warning'
                              : 'default'
                        }
                        size="sm"
                      >
                        {post.status === 'published'
                          ? intl.formatMessage({ id: 'hiring.my_posts.post.status.active' })
                          : post.status === 'draft'
                            ? intl.formatMessage({ id: 'hiring.my_posts.post.status.draft' })
                            : intl.formatMessage({ id: 'hiring.my_posts.post.status.closed' })}
                      </Badge>
                    </div>

                    <h3 className="text-lg font-semibold text-neutral-900 mb-2">{post.title}</h3>

                    <div className="flex flex-wrap gap-4 text-sm text-neutral-600 mb-3">
                      <div className="flex items-center gap-2">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{post.location}</span>
                        {post.remote && (
                          <Badge size="sm" variant="info">{intl.formatMessage({ id: 'hiring.my_posts.post.remote' })}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{intl.formatMessage({ id: 'hiring.my_posts.post.posted' }, { date: formatDate(post.publishedAt || post.createdAt) })}</span>
                      </div>
                    </div>

                    {/* Metrics */}
                    {post.status === 'published' && (
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <svg className="h-4 w-4 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span className="font-medium text-neutral-900">{post.viewsCount || 0}</span>
                          <span className="text-neutral-500">{intl.formatMessage({ id: 'hiring.my_posts.metrics.views' })}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <svg className="h-4 w-4 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <span className="font-medium text-neutral-900">{post.conversationsCount || 0}</span>
                          <span className="text-neutral-500">{intl.formatMessage({ id: 'hiring.my_posts.metrics.conversations' })}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-neutral-900">{post.savedCount || 0}</span>
                          <span className="text-neutral-500">{intl.formatMessage({ id: 'hiring.my_posts.metrics.saved' })}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button as="a" href={`/hiring/${post.id}`} size="sm" variant="outline">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {intl.formatMessage({ id: 'hiring.my_posts.actions.view' })}
                    </Button>
                    <Button as="a" href={`/hiring/${post.id}/edit`} size="sm" variant="outline">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      {intl.formatMessage({ id: 'hiring.my_posts.actions.edit' })}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={
              <svg className="h-full w-full text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
            title={
              activeTab === 'active'
                ? intl.formatMessage({ id: 'hiring.my_posts.empty.active_title' })
                : activeTab === 'draft'
                  ? intl.formatMessage({ id: 'hiring.my_posts.empty.draft_title' })
                  : intl.formatMessage({ id: 'hiring.my_posts.empty.closed_title' })
            }
            description={
              activeTab === 'active'
                ? intl.formatMessage({ id: 'hiring.my_posts.empty.active_description' })
                : activeTab === 'draft'
                  ? intl.formatMessage({ id: 'hiring.my_posts.empty.draft_description' })
                  : intl.formatMessage({ id: 'hiring.my_posts.empty.closed_description' })
            }
            action={
              activeTab !== 'closed'
                ? (
                  <Button as="a" href="/hiring/new" size="md">
                    <Plus className="w-4 h-4" />
                    {intl.formatMessage({ id: 'hiring.my_posts.actions.create_new' })}
                  </Button>
                )
                : undefined
            }
          />
        )}
    </>
  );

  if (hideHeader) return content;

  return (
    <SingleColumnTemplate
      title={intl.formatMessage({ id: 'hiring.my_posts.title' })}
      subtitle={intl.formatMessage({ id: 'hiring.my_posts.subtitle' })}
      actions={
        <Button as="a" href="/hiring/new" size="lg">
          <Plus className="h-5 w-5" />
          {intl.formatMessage({ id: 'hiring.my_posts.actions.create_new' })}
        </Button>
      }
    >
      {content}
    </SingleColumnTemplate>
  );
};

export default MyHiringPosts;
